import { useEffect, useState } from 'react';
import {
    Alert,
    Button,
    ButtonGroup,
    Col,
    Form,
    FormGroup,
    FormText,
    FormFeedback,
    Input,
    Label,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
    Row
} from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import AddPimsDependencyModal from './AddPimsDependencyModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import { v4 as uuidv4 } from 'uuid';
import { actions } from '../../../../context';
import SuccessConfirmationModal from '../../SuccessConfirmationModal';
import BtxSwitch from '../../../Forms/BtxSwitch';
import moment from 'moment';

const defaultPlugin = {
    id: '',
    name: null,
    defaultStartupTimeout: 0,
    pimsDependencies: [],
    defaultStatus: '',
    isInactive: false,
};

const AddPluginModal = (props) => {
    const { isOpen, closeModalBehavior, existingPlugin = null } = props;

    const dispatch = useDispatch();

    const [pluginToAdd, setPluginToAdd] = useState(
        existingPlugin ?? defaultPlugin
    );
    const [isIdValid, setIsIdValid] = useState(true);
    const [isDefaultStartupValid, setIsDefaultStartupTimeoutValid] =
        useState(true);
    const [isAddPimsDependencyModalOpen, setIsAddPimsDependencyModalOpen] =
        useState(false);
    const [isSuccessConfirmationModalOpen, setIsSuccessConfirmationModalOpen] =
        useState(false);
    const [requestResponse, setRequestResponse] = useState();
    const [isAwaitingResponse, setIsAwaitingResponse] = useState(false);
    const [pluginVersions, setPluginVersions] = useState([]);
    const [existingPlugins, setExistingPlugins] = useState([]);
    const [showReleaseTime, setShowReleaseTime] = useState(false);
    const [releaseTime, setReleaseTime] = useState(null);
    const tz = moment().tz(moment.tz.guess()).zoneAbbr();

    useEffect(() => {
        setPluginToAdd(existingPlugin ?? defaultPlugin);
        setReleaseTime(null);
        setShowReleaseTime(false);

        if (existingPlugin) {
            dispatch(
                actions.missionControl.getPluginVersions(existingPlugin.id)
            ).then((res) => {
                setPluginVersions(res || []);
            });
        } else {
            dispatch(actions.missionControl.getPlugins()).then((res) => {
                setExistingPlugins(res.data?.map((p) => p.id) || []);
            });
        }
    }, [isOpen, existingPlugin]);

    const pims = useSelector((state) => state.pims.allPims);

    const handlePluginIdChange = (e) => {
        setPluginToAdd({ ...pluginToAdd, id: e.target.value });
        const isValidUuid = isUUIDFormat(e.target.value);
        const isExistingPlugin = existingPlugins.indexOf(e.target.value) !== -1;

        setIsIdValid(isValidUuid && (!isExistingPlugin || existingPlugin));
    };

    const handlePluginNameChange = (e) => {
        setPluginToAdd({ ...pluginToAdd, name: e.target.value });
    };

    const handleDefaultStartupTimeoutChange = (e) => {
        setPluginToAdd({
            ...pluginToAdd,
            defaultStartupTimeout: e.target.value,
        });
        setIsDefaultStartupTimeoutValid(isANumber(e.target.value));
    };

    const handlePimsDependenciesChange = (value) => {
        if (
            pluginToAdd.pimsDependencies.some(
                (el) =>
                    el.pimsId === value.pimsId &&
                    el.minimumPimsVersionId === value.minimumPimsVersionId
            )
        )
            return;

        setPluginToAdd({
            ...pluginToAdd,
            pimsDependencies: [...pluginToAdd.pimsDependencies, value],
        });
    };

    const removePimsDependency = (pimsDependency) => {
        setPluginToAdd({
            ...pluginToAdd,
            pimsDependencies: pluginToAdd.pimsDependencies.filter(
                (pims) =>
                    !(
                        pims.pimsId === pimsDependency.pimsId &&
                        pims.minimumPimsVersionId ===
                            pimsDependency.minimumPimsVersionId
                    )
            ),
        });
    };

    const handleDefaultStatusChange = (e) => {
        let discreteStatus = {};
        switch (e.target.value) {
            case 'present in manifest':
                discreteStatus = {
                    isCached: false,
                    isInstalled: false,
                    isRunning: false,
                };
                break;
            case 'cached':
                discreteStatus = {
                    isCached: true,
                    isInstalled: false,
                    isRunning: false,
                };
                break;
            case 'installed':
                discreteStatus = {
                    isCached: true,
                    isInstalled: true,
                    isRunning: false,
                };
                break;
            case 'running':
                discreteStatus = {
                    isCached: true,
                    isInstalled: true,
                    isRunning: true,
                };
                break;
        }

        setPluginToAdd({ ...pluginToAdd, defaultStatus: discreteStatus });
    };

    const handleIsInactiveChange = (e) => {
        setPluginToAdd({ ...pluginToAdd, isInactive: !pluginToAdd.isInactive });
    };

    const handleDefaultVersionChange = (e) => {
        let versionInfo = JSON.parse(e.target.value); // ran into issues trying to store the object directly in the options list
        setPluginToAdd({
            ...pluginToAdd,
            defaultVersion: {
                ...pluginToAdd.defaultVersion,
                id: versionInfo.id,
                version: versionInfo.version
            },
        });
    };

    const handleShowReleaseTimeToggle = (e) => {
        setShowReleaseTime(!showReleaseTime);
    }

    const handleReleaseTimeChange = (e) => {
        let time = moment(e.target.value).utc().format('YYYY-MM-DD HH:mm:ss');
        setReleaseTime(time);
    }

    const handleCloseModal = () => {
        setPluginToAdd(defaultPlugin);
        closeModalBehavior();
    };

    const handleSubmit = () => {
        setIsAwaitingResponse(true);
        if (
            (!isIdValid && pluginToAdd.id !== '') ||
            (!isDefaultStartupValid &&
                pluginToAdd.defaultStartupTimeout !== '') ||
            pluginToAdd.defaultStatus === ''
        ) {
            //Invalid
            setRequestResponse({
                isSuccess: false,
                message: 'One or more fields are invalid',
            });
            setIsSuccessConfirmationModalOpen(true);
            setIsAwaitingResponse(false);
        } else {
            var req;
            if (existingPlugin) {
                req = {
                    id: pluginToAdd.id,
                    name: pluginToAdd.name,
                    defaultVersionId: pluginToAdd.defaultVersion?.id,
                    defaultVersion: pluginToAdd.defaultVersion?.version,
                    defaultStartupTimeout: pluginToAdd.defaultStartupTimeout,
                    pimsDependencies: pluginToAdd.pimsDependencies,
                    isInactive: pluginToAdd.isInactive,
                    defaultStatus: pluginToAdd.defaultStatus,
                    releaseTime: releaseTime
                };
            } else {
                req = {
                    id: pluginToAdd.id === '' ? uuidv4() : pluginToAdd.id,
                    name: pluginToAdd.name,
                    defaultVersionId: null, //Not set on plugin creation
                    defaultStartupTimeout: pluginToAdd.defaultStartupTimeout,
                    pimsDependencies: pluginToAdd.pimsDependencies,
                    isInactive: false, //Not set on plugin creation
                    defaultStatus: pluginToAdd.defaultStatus,
                };
            }

            dispatch(actions.missionControl.addOrUpdatePlugin(req))
                .then((res) => {
                    setRequestResponse(res);

                    if (res?.isSuccess) {
                        setPluginToAdd(defaultPlugin);
                        closeModalBehavior();
                    }
                })
                .then(() => {
                    setIsSuccessConfirmationModalOpen(true);
                    setIsAwaitingResponse(false);
                });
        }
    };

    const getModalHeaderText = () => {
        return existingPlugin ? 'Edit plugin' : 'Create New Plugin';
    };

    const getPluginIdFormGroup = () => {
        return existingPlugin ? (
            <FormGroup>
                <Label>{'Plugin ID'}</Label>
                <Input
                    value={pluginToAdd?.id || ''}
                    readOnly
                    onChange={handlePluginIdChange}
                />
            </FormGroup>
        ) : (
            <FormGroup>
                <Label>{'Plugin ID'}</Label>
                <Input
                    invalid={pluginToAdd.id !== '' && !isIdValid}
                    valid={pluginToAdd.id !== '' && isIdValid}
                    disabled={isAwaitingResponse}
                    value={pluginToAdd?.id || ''}
                    onChange={handlePluginIdChange}
                />
                <FormText color="warning">
                    {'Id will be generated if not provided'}
                </FormText>
                <FormFeedback invalid>
                    Invalid UUID format or is an ID for an existing plugin.
                </FormFeedback>
            </FormGroup>
        );
    };

    const getPluginNameFormGroup = () => {
        return (
            <FormGroup>
                <Label>{'Plugin Name'}</Label>
                <Input
                    disabled={isAwaitingResponse}
                    value={pluginToAdd?.name || ''}
                    onChange={handlePluginNameChange}
                />
            </FormGroup>
        );
    };

    const getDefaultStartupTimeoutFormGroup = () => {
        return (
            <FormGroup>
                <Label>{'Default Startup Timeout (in seconds)'}</Label>
                <Input
                    invalid={
                        pluginToAdd.defaultStartupTimeout !== '' &&
                        !isDefaultStartupValid
                    }
                    valid={
                        pluginToAdd.defaultStartupTimeout !== '' &&
                        isDefaultStartupValid
                    }
                    disabled={isAwaitingResponse}
                    value={pluginToAdd?.defaultStartupTimeout || 0}
                    onChange={handleDefaultStartupTimeoutChange}
                />
                <FormFeedback invalid="true">
                    Default startup timeout must be a number.
                </FormFeedback>
            </FormGroup>
        );
    };

    const getPimsDependenciesFormGroup = () => {
        return (
            <FormGroup>
                <Label>{'PIMS Dependencies'}</Label>
                <br />
                <Button
                    color="primary"
                    disabled={isAwaitingResponse}
                    onClick={() => setIsAddPimsDependencyModalOpen(true)}>
                    Add PIMS Dependency
                </Button>
                <br />
                <ButtonGroup vertical style={{ margin: '6px 0px' }}>
                    {GetAddedPimsDependencies(
                        pluginToAdd,
                        pims,
                        removePimsDependency
                    )}
                </ButtonGroup>
            </FormGroup>
        );
    };

    const getDefaultStatusFormGroup = () => {
        return existingPlugin ? (
            <FormGroup>
                <Label>{'Default Status'}</Label>
                <Input
                    type="select"
                    disabled={isAwaitingResponse}
                    onChange={handleDefaultStatusChange}
                    defaultValue={getPluginGeneralStatus(
                        pluginToAdd.defaultStatus
                    )}>
                    <option value="present in manifest">
                        {'Present in manifest'}
                    </option>
                    <option value="cached">{'Cached'}</option>
                    <option value="installed">{'Installed'}</option>
                    <option value="running">{'Running'}</option>
                </Input>
            </FormGroup>
        ) : (
            <FormGroup>
                <Label>{'Default Status'}</Label>
                <Input
                    type="select"
                    disabled={isAwaitingResponse}
                    onChange={handleDefaultStatusChange}
                    defaultValue={''}>
                    <option disabled value="">
                        --Select a status--
                    </option>
                    <option value="present in manifest">
                        {'Present in manifest'}
                    </option>
                    <option value="cached">{'Cached'}</option>
                    <option value="installed">{'Installed'}</option>
                    <option value="running">{'Running'}</option>
                </Input>
            </FormGroup>
        );
    };

    const getIsInvalidFormGroup = () => {
        if (existingPlugin) {
            return (
                <FormGroup>
                    <Label>{'Is Active'}</Label>
                    <BtxSwitch
                        value={!pluginToAdd?.isInactive || true}
                        onChange={handleIsInactiveChange}></BtxSwitch>
                </FormGroup>
            );
        }
    };

    const getDefaultPluginVersionFromGroup = () => {
        if (existingPlugin) {

            const sortedVersions = pluginVersions
                .slice()   // → don’t mutate the original
                .sort((a, b) =>
                    new Date(b.releaseDate).getTime() - new Date(a.releaseDate).getTime()
                );
            
            return (
                <FormGroup>
                    <Label>{'Default Plugin Version'}</Label>
                    <Input
                        type="select"
                        disabled={isAwaitingResponse}
                        onChange={handleDefaultVersionChange}
                        defaultValue={existingPlugin.defaultVersion ? JSON.stringify(sortedVersions.find((x, y, z) => x.id === existingPlugin.defaultVersion.id)) : ""}>
                        <option disabled value="">
                            --No default version set--
                        </option>
                        {sortedVersions.map((ver) => (
                            <option key={ver?.id} value={JSON.stringify(ver)}>
                                {getVersionInfo(ver)}
                            </option>
                        ))}
                    </Input>
                </FormGroup>
            );
        }
    };

    const getShowReleaseTimeFormGroup = () => {
        if (existingPlugin) {
            let oldStatus = getPluginGeneralStatus(existingPlugin.defaultStatus);
            let newStatus = getPluginGeneralStatus(pluginToAdd.defaultStatus);

            return (
                <FormGroup>
                    <Label>{'Schedule Release?'}</Label>
                    <BtxSwitch
                        value={showReleaseTime ?? new Date()}
                        disabled={(existingPlugin.defaultVersion?.id === pluginToAdd?.defaultVersion?.id) && oldStatus === newStatus}
                        onChange={handleShowReleaseTimeToggle}
                    ></BtxSwitch>
                </FormGroup>
            )
        }
        
    }

    const getReleaseTimeFormGroup = () => {
        if (existingPlugin && showReleaseTime) {
            return (
                <FormGroup>
                    <Label>{`Schedule Release Time (${tz})`}</Label>
                    <Input
                        type="datetime-local"
                        disabled={isAwaitingResponse}
                        onChange={handleReleaseTimeChange}
                        defaultValue={releaseTime}
                    ></Input>
                    {getWarning()}
                </FormGroup>
            );
        }
    }

    const getWarning = () => {
        if (existingPlugin && existingPlugin.soonestScheduledRelease !== null) {
            return (
                <Alert className='mt-2' color="warning">This plugin already has a scheduled release time of {moment
                    .utc(existingPlugin.soonestScheduledRelease)
                    .local()
                    .format('M/D/YYYY LT') + ' ' + tz}</Alert>
            )
        }
    }

    return (
        <>
            <SuccessConfirmationModal
                isOpen={isSuccessConfirmationModalOpen}
                closeBehavior={() => setIsSuccessConfirmationModalOpen(false)}
                successResponse={requestResponse}
            />
            <Modal isOpen={isOpen} centered className="text-light">
                <AddPimsDependencyModal
                    isOpen={isAddPimsDependencyModalOpen}
                    pims={pims}
                    closeBehavior={() => setIsAddPimsDependencyModalOpen(false)}
                    addDependencyBehavior={handlePimsDependenciesChange}
                />
                <ModalHeader>{getModalHeaderText()}</ModalHeader>
                <ModalBody>
                    <Form>
                        {getPluginIdFormGroup()}
                        {getPluginNameFormGroup()}
                        {getDefaultPluginVersionFromGroup()}
                        {getDefaultStartupTimeoutFormGroup()}
                        {getDefaultStatusFormGroup()}
                        {getPimsDependenciesFormGroup()}
                        <Row>
                            <Col md="3">
                                {getIsInvalidFormGroup()}
                            </Col>
                            <Col md="6">
                                {getShowReleaseTimeFormGroup()}
                            </Col>
                        </Row>
                        {getReleaseTimeFormGroup()}
                    </Form>
                </ModalBody>
                <ModalFooter>
                    <Button
                        color="danger"
                        disabled={isAwaitingResponse}
                        onClick={() => handleCloseModal()}>
                        Cancel
                    </Button>
                    <Button
                        color={isAwaitingResponse ? 'secondary' : 'success'}
                        disabled={isAwaitingResponse}
                        onClick={() => handleSubmit()}>
                        {isAwaitingResponse ? 'Saving...' : 'Save'}
                    </Button>
                </ModalFooter>
            </Modal>
        </>
    );
};

function GetAddedPimsDependencies(pluginToAdd, pims, removePimsDependency) {
    return pluginToAdd?.pimsDependencies?.map((pd) => {
        return (
            <ButtonGroup id={pd.pimsId} style={{ margin: '4px 6px' }}>
                <Button disabled>
                    {pims.find((el) => el.id === pd.pimsId).name}
                </Button>
                <Button onClick={() => removePimsDependency(pd)}>
                    <FontAwesomeIcon icon={faTrash} />
                </Button>
            </ButtonGroup>
        );
    });
}

function isUUIDFormat(str) {
    const uuidRegex =
        /^[0-9a-f]{8}-[0-9a-f]{4}-[4][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    return uuidRegex.test(str);
}

function isANumber(str) {
    return !isNaN(str);
}

function getPluginGeneralStatus(discreteStatus) {
    if (discreteStatus?.isRunning) return 'running';
    if (discreteStatus?.isInstalled) return 'installed';
    if (discreteStatus?.isCached) return 'cached';

    return 'present in manifest';
}

function getVersionInfo(pluginVersion) {
    if (!pluginVersion) {
        return '';
    }

    return `Version: ${pluginVersion.version} (Released: ${moment
        .utc(pluginVersion.releaseDate)
        .local()
        .format('M/D/YYYY LT')}) - ${pluginVersion.description}`;
}

export default AddPluginModal;
